import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { EditOtherForm } from 'components/EditOtherForm';
import { useEditorParseData } from 'components/FormulaEditor';
import { ModalDialog } from 'components/ModalDialog';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type {
    ConstructionTypeData,
    // ContractOtherCostGroupConstructionLimitData,
    ContractOtherUpdateInput,
} from 'gql/graphql';
import { OtherCostCalculationTypeEnum, ProjectMethodologyEnum } from 'gql/graphql';
import { getOtherFormulaSourceObjectGql } from 'pages/Project/Common/Contract/contractQueries';
import type { TypeItem } from 'pages/Project/Common/Contract/OtherCreate/commonData';
import {
    OTHER_SECTION_OR_CHAPTER,
    seasonByDistributionMethod,
    SeasonItemsIdsEnum,
    TabsIds,
    tabsItems,
    TYPE_VARIANTS,
} from 'pages/Project/Common/Contract/OtherCreate/commonData';
import { uerContractOtherCostForUpdateGql } from 'pages/Project/Common/Contract/OtherEdit/editOtherQueries';
import { prepareLimitsData } from 'pages/Project/Common/hooks/prepareLimitsData';
import {
    type IContractOtherCostLimitSpanSelectionItem,
    useUerContractOtherCostLimitSpanSelectionList,
} from 'pages/Project/UerProject/Contract/hooks/useUerContractOtherCostLimitSpanSelectionList';
import { replacerCost } from 'utils/replacerCost';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { uerUpdateContractOtherCostToWorkOrderChangeFromPriceListGql } from '../WorkOrderChangeQueries';

const fieldsToCheck = {
    [TabsIds.COMMON]: ['name', 'calculatingTypeValue', 'otherCostType'],
    [TabsIds.PRICE]: ['cost', 'method'],
    [TabsIds.FORMULA]: ['formula'],
};

export const EditWorkOrderChangeOtherDialog = ({
    isOpened,
    closeModalHandler,
    contractId,
    currentConstructionTypeId,
    entryId,
    constructionTypes,
    refreshOtherTableHandler,
    workOrderOtherPriceId,
    workOrderChangeOtherPriceId,
    costRounding,
    isReadOnly,
}: {
    isOpened: boolean;
    closeModalHandler: () => void;
    contractId: number;
    currentConstructionTypeId: number;
    entryId: number;
    constructionTypes: ConstructionTypeData[];
    refreshOtherTableHandler: () => void;
    workOrderOtherPriceId: number;
    workOrderChangeOtherPriceId: number;
    costRounding: number;
    isReadOnly: boolean;
}) => {
    const dispatch = useDispatch();
    const formRef = useRef<HTMLFormElement>(null);
    const form = useForm({
        shouldUnregister: false,
    });

    const { control, handleSubmit, reset, formState, setError, clearErrors, setValue } = form;

    const [isOpenWarningDialog, setIsOpenWarningDialog] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState(tabsItems[0]);
    const [selectedType, setSelectedType] = useState<TypeItem>(TYPE_VARIANTS[0]);
    const [selectedChapterOrSection, setSelectedChapterOrSection] = useState(OTHER_SECTION_OR_CHAPTER[0]);
    const [selectedConstructionType, setSelectedConstructionType] = useState<Record<string, boolean>>({
        [currentConstructionTypeId]: true,
    });
    const { deserializeFromBackend, serializeStringForBackend } = useEditorParseData({});

    const otherRowDataVariables = useMemo(
        () => ({
            contractId,
            contractOtherCostId: entryId,
        }),
        [entryId, contractId],
    );

    const [otherRowData, otherForUpdateStatus] = useRequest({
        requestId: 'workOrderChangeOtherForUpdate',
        variables: otherRowDataVariables,
        query: uerContractOtherCostForUpdateGql,
        withEffect: !!(contractId && entryId),
    });

    const spanSelectionData = useUerContractOtherCostLimitSpanSelectionList({
        withEffect: true,
        contractId,
        otherCostId: entryId,
        constructionTypeId: currentConstructionTypeId,
    });

    const [resetOtherRowData] = useResetStoreData({ requestId: 'workOrderChangeOtherForUpdate' });
    const [resetUpdateResultData] = useResetStoreData({ requestId: 'updateWorkOrderChangeOther' });

    const isOtherDataFetching = useMemo(() => otherForUpdateStatus === RequestStatus.FETCHING, [otherForUpdateStatus]);

    const resetAllEditModalData = useCallback(() => {
        resetOtherRowData(undefined);
        resetUpdateResultData(undefined);
    }, [resetOtherRowData, resetUpdateResultData]);

    const handleChangeConstructionType = useCallback((id, state) => {
        setSelectedConstructionType((types) => ({
            ...types,
            [id]: state,
        }));
    }, []);

    useEffect(() => {
        if (otherRowData) {
            const { data } = otherRowData?.uerContractOtherCostForUpdate || {};
            const currentType =
                data?.calculatingType === OtherCostCalculationTypeEnum.Calculation
                    ? TYPE_VARIANTS[0]
                    : TYPE_VARIANTS[1];

            const limitsValues = data?.groupConstructionLimits.reduce(
                (
                    limits: {
                        cost: { [key: number]: number };
                        method: { [key: number]: Record<string, string> };
                        manualDistribution: { [key: number]: Record<`season_${number}`, string> };
                        limitSpanInputs: { [key: number]: IContractOtherCostLimitSpanSelectionItem[] };
                        hasActualCost: { [key: number]: boolean };
                        hasLimitSpan: { [key: number]: boolean };
                    },
                    item: any,
                    // Omit<
                    //     ContractOtherCostGroupConstructionLimitData,
                    //     'isInUse' | 'canEditCost' | 'canExclude' | 'linkageStatus'
                    // > ,
                ) => {
                    const currentId = item.constructionType.id;

                    handleChangeConstructionType(currentId, true);

                    limits.limitSpanInputs[currentId] = spanSelectionData.data.filter(
                        (limitItem) => limitItem.constructionTypeId === currentId && limitItem.isSelected,
                    );
                    limits.cost[currentId] = item.cost;
                    limits.method[currentId] = seasonByDistributionMethod[item.distributionMethod];
                    limits.hasActualCost[currentId] = item.hasActualCost;
                    limits.hasLimitSpan[currentId] = Boolean(item.hasLimitSpan);

                    if (seasonByDistributionMethod[item.distributionMethod]?.id === SeasonItemsIdsEnum.MANUAL) {
                        data?.limits?.forEach((limit) => {
                            if (limit.constructionType?.id === currentId) {
                                limits.manualDistribution[currentId] = {
                                    ...limits.manualDistribution[currentId],
                                    [`season_${limit.season?.id}`]: limit.cost,
                                };
                            }
                        });
                    }

                    return limits;
                },
                {
                    cost: {},
                    method: {},
                    manualDistribution: [],
                    limitSpanInputs: {},
                    hasActualCost: {},
                    hasLimitSpan: {},
                },
            );

            const calculatingTypeValue = data?.calculatingTypeValue;
            const chapter = data?.chapter;
            const otherCostType = data?.catalogOtherCostType;
            const formula = deserializeFromBackend(data?.formula);

            setSelectedType(currentType);
            setSelectedChapterOrSection((chapter ? OTHER_SECTION_OR_CHAPTER[0] : OTHER_SECTION_OR_CHAPTER[1]) as any);

            reset({
                name: otherRowData?.uerContractOtherCostForUpdate?.data?.name,
                ...limitsValues,
                ...(calculatingTypeValue && { calculatingTypeValue }),
                otherCostType,
                formula,
                key: data?.key,
            });
        }
    }, [deserializeFromBackend, handleChangeConstructionType, otherRowData, reset, spanSelectionData.data]);

    useEffect(
        () => () => {
            resetAllEditModalData();
        },
        [resetAllEditModalData],
    );

    const successUpdateHandler = useCallback(() => {
        refreshOtherTableHandler?.();
        closeModalHandler?.();
        resetAllEditModalData();
    }, [closeModalHandler, refreshOtherTableHandler, resetAllEditModalData]);

    const [, updateResultStatus, , updateRequest] = useRequest({
        requestId: 'updateWorkOrderChangeOther',
        query: uerUpdateContractOtherCostToWorkOrderChangeFromPriceListGql,
        withEffect: false,
        dependenciesUpdater: successUpdateHandler,
    });

    const editHandler = useCallback(
        (data) => {
            const { name, calculatingTypeValue, formula, otherCostType } = data;

            if (replacerCost(calculatingTypeValue) < 0) {
                setActiveTab(tabsItems[0]);
                setTimeout(() => {
                    setError('calculatingTypeValue', { message: 'Процент должен быть не отрицательным числом' });
                }, 0);

                return;
            }

            const { limitsArray, haveError, errorMsgObj } = prepareLimitsData(selectedConstructionType, data, true);

            if (haveError) {
                setActiveTab(tabsItems[1]);

                if (errorMsgObj) {
                    Object.entries(errorMsgObj).forEach(([id, errorMessage]) => {
                        setError(`method.${id}`, { message: errorMessage });
                    });
                }

                return;
            }

            const variables: { input: ContractOtherUpdateInput; workOrderChangeOtherPriceId: number } = {
                workOrderChangeOtherPriceId,
                input: {
                    id: workOrderOtherPriceId,
                    contractId,
                    name,
                    calculatingTypeValue: replacerCost(calculatingTypeValue),
                    limits: limitsArray,
                    ...(formula ? { formula: serializeStringForBackend(formula) } : {}),
                    catalogOtherCostTypeId: otherCostType?.id,
                },
            };

            dispatch(updateRequest({ ...variables }));
        },
        [
            contractId,
            dispatch,
            selectedConstructionType,
            serializeStringForBackend,
            setError,
            updateRequest,
            workOrderChangeOtherPriceId,
            workOrderOtherPriceId,
        ],
    );

    const onCancel = useCallback(() => {
        if (!isReadOnly && formState.isDirty) {
            setIsOpenWarningDialog(true);
        } else {
            closeModalHandler();
        }
    }, [closeModalHandler, formState.isDirty, isReadOnly]);

    const handleTabChange = useCallback(({ value }) => setActiveTab(value), [setActiveTab]);

    const handleChangeType = useCallback((newType) => {
        setSelectedType(newType);
    }, []);

    const changeSelectedChapterOrSection = useCallback((value) => {
        setSelectedChapterOrSection(value);
    }, []);

    const handleSubmitWarningDialog = useCallback(() => {
        closeModalHandler();
    }, [closeModalHandler]);

    const handleCancelWarningDialog = useCallback(() => {
        setIsOpenWarningDialog(false);
    }, []);

    return (
        <ModalDialog
            title={`${isReadOnly ? 'Просмотр' : 'Редактирование'} прочих затрат`}
            onConfirm={isReadOnly ? undefined : handleSubmit(editHandler)}
            onClose={onCancel}
            isOpen={isOpened}
            withTitleCloseButton
            hasOverlay
            btnConfirmDisabled={!formState.isDirty}
            isCommit={updateResultStatus === RequestStatus.FETCHING}
            isOnClickOutsideDisable
            btnCancelView={isReadOnly ? 'primary' : undefined}
            btnCancelLabel={isReadOnly ? 'Закрыть' : undefined}
        >
            <FormProvider {...form}>
                <EditOtherForm
                    activeTab={activeTab}
                    handleTabChange={handleTabChange}
                    tabItems={tabsItems}
                    control={control}
                    fieldsToCheck={fieldsToCheck}
                    isDataFetching={isOtherDataFetching}
                    chapter={otherRowData?.uerContractOtherCostForUpdate?.data?.chapter}
                    selectedType={selectedType}
                    handleChangeType={handleChangeType}
                    areFieldsDisabled={false}
                    isChapterEditable={false}
                    isCalculationTypeEditable={false}
                    changeSelectedChapterOrSection={changeSelectedChapterOrSection}
                    selectedChapterOrSection={selectedChapterOrSection}
                    currentConstructionTypeId={currentConstructionTypeId}
                    selectedConstructionType={selectedConstructionType}
                    handleChangeConstructionType={handleChangeConstructionType}
                    constructionTypes={constructionTypes}
                    ref={formRef}
                    isGlobal={otherRowData?.uerContractOtherCostForUpdate?.data?.isGlobalOtherCost}
                    otherStatus={otherRowData?.uerContractOtherCostForUpdate?.data?.status}
                    methodology={ProjectMethodologyEnum.Uer}
                    globalOtherName={otherRowData?.uerContractOtherCostForUpdate?.data?.name}
                    getObjectSourceQuery={getOtherFormulaSourceObjectGql}
                    costRounding={costRounding}
                    clearErrors={clearErrors}
                    isModalReadOnly={isReadOnly}
                    setValue={setValue}
                    spanSelectionData={spanSelectionData}
                    otherId={entryId}
                />
            </FormProvider>
            <ConfirmationDialog
                textHeader="ОТМЕНА РЕДАКТИРОВАНИЯ ПРОЧИХ ЗАТРАТ"
                textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                confirmButtonText="Отменить"
                cancelButtonText="Не отменять"
                onSubmit={handleSubmitWarningDialog}
                isOpened={isOpenWarningDialog}
                onClose={handleCancelWarningDialog}
            />
        </ModalDialog>
    );
};

EditWorkOrderChangeOtherDialog.displayName = 'EditWorkOrderChangeOtherDialog';
